import Service from 'react-reservice';

import marketHideAPI, { EHideType } from '@webtrader/core/api/market/hide';
import symbolsAPI, { ESymbolType, ISymbol } from '@webtrader/core/api/market/symbols';
import { EMarketFilter, TSymbolFilter } from '@webtrader/core/constants/symbol';
import { filterSymbolsByMarketFilter } from '@webtrader/core/format/symbol';
import i18n from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';
import { symbolsService } from '@webtrader/core/services/symbols';

interface ISymbolsPreferenceContext {
  dirty: boolean;
  saving: boolean;
  loading: boolean;
  keyword: string;
  allSymbols: ISymbol[];
  checkedSymbols: ISymbol[];
  selectedMarketFilter: EMarketFilter;
  originalCheckedSymbols: ISymbol[];
}

interface ISaveSymbolsPreferenceParams {
  showAll: boolean;
  type: EHideType;
  values: string[];
}

const defaultContext: ISymbolsPreferenceContext = {
  dirty: false,
  saving: false,
  loading: true,
  keyword: '',
  allSymbols: [],
  checkedSymbols: [],
  selectedMarketFilter: EMarketFilter.CRYPTO,
  originalCheckedSymbols: []
};

class SymbolsPreferenceService extends Service<ISymbolsPreferenceContext> {
  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = () => {
    this.resetSelectedMarketFilter();
    this.fetchMarketAllSymbols();
  };

  public reset = () => {
    this.context.dirty = defaultContext.dirty;
    this.context.saving = defaultContext.saving;
    this.context.keyword = defaultContext.keyword;
    this.context.loading = defaultContext.loading;

    this.resetSelectedMarketFilter();
  };

  public onSaveSymbolsPreference = async (params: ISaveSymbolsPreferenceParams) => {
    const { showAll, type, values } = params;

    try {
      await marketHideAPI.hide({ showAll, type, values });
    } catch {}
  };

  public setCheckedSymbols = (symbols: ISymbol[]) => {
    this.context.checkedSymbols = [...symbols];
    this.context.originalCheckedSymbols = [...symbols];
  };

  public onSelectFilter = (filter: EMarketFilter) => {
    this.context.selectedMarketFilter = filter;
  };

  public onCheckAll = (checkedAll: boolean) => {
    const { allSymbols, checkedSymbols, selectedMarketFilter } = this.context;

    if (checkedAll) {
      this.context.checkedSymbols = this.removeCheckedSymbolsBySelectedFilter();
    } else {
      const newCheckedSymbols = [...checkedSymbols];
      const filteredSymbols = filterSymbolsByMarketFilter(selectedMarketFilter, allSymbols);

      filteredSymbols.forEach((symbol) => {
        if (newCheckedSymbols.findIndex((s) => s.symbol === symbol.symbol) === -1) {
          newCheckedSymbols.push(symbol);
        }
      });

      this.context.checkedSymbols = [...newCheckedSymbols];
    }

    this.checkDirty();
  };

  public setKeyword = (keyword: string) => {
    this.context.keyword = keyword;
  };

  public onSelectSymbolOption = (symbol: ISymbol, checked: boolean) => {
    if (checked) {
      this.context.checkedSymbols = this.context.checkedSymbols.filter((s) => s.symbol !== symbol.symbol);
    } else {
      this.context.checkedSymbols = [...this.context.checkedSymbols, symbol];
    }

    this.checkDirty();
  };

  public onShowSymbolsPreference = () => {
    if (this.context.allSymbols.length === 0) {
      this.fetchMarketAllSymbols();
    }
  };

  public resetCheckedSymbols = () => {
    this.context.checkedSymbols = [...this.context.originalCheckedSymbols];
  };

  public saveChanges = async () => {
    const { allSymbols, checkedSymbols } = this.context;

    const symbolsToHide: ISymbol[] = [];

    allSymbols.forEach((symbol) => {
      if (checkedSymbols.findIndex((s) => s.symbol === symbol.symbol) === -1) {
        symbolsToHide.push(symbol);
      }
    });

    try {
      this.context.saving = true;

      await marketHideAPI.hide({ showAll: false, type: EHideType.SYMBOL, values: symbolsToHide.map((s) => s.symbol) });

      this.context.originalCheckedSymbols = [...checkedSymbols];

      return {
        success: true,
        message: i18n.t('All changes are saved.')
      };
    } catch (err) {
      const { code, message } = err as IApiError;

      return {
        success: false,
        message: mapResponseCodeToMessage(code, message)
      };
    } finally {
      this.context.saving = false;
      this.checkDirty();
    }
  };

  public checkDirty = () => {
    const checkedSymbolsNames = this.context.checkedSymbols.map(({ symbol }) => symbol);
    const originalCheckedSymbolsNames = this.context.originalCheckedSymbols.map(({ symbol }) => symbol);

    this.context.dirty =
      JSON.stringify(checkedSymbolsNames.sort(this.sortBySymbolName)) !==
      JSON.stringify(originalCheckedSymbolsNames.sort(this.sortBySymbolName));
  };

  public clearAllSymbols = () => {
    this.context.allSymbols = [];
  };

  private sortBySymbolName = (a: string, b: string) => (a > b ? 1 : -1);

  private removeCheckedSymbolsBySelectedFilter = () => {
    const { checkedSymbols, selectedMarketFilter } = this.context;

    return checkedSymbols.filter((symbol) => {
      const { type } = symbol;

      switch (selectedMarketFilter) {
        case EMarketFilter.FOREX:
          return type !== ESymbolType.FOREX;
        case EMarketFilter.INDEX:
          return type !== ESymbolType.INDEX;
        case EMarketFilter.CRYPTO:
          return type !== ESymbolType.CRYPTO;
        case EMarketFilter.SHARES:
          return type !== ESymbolType.SHARES;
        case EMarketFilter.COMMODITIES:
          return type !== ESymbolType.COMMODITIES;
        default:
          return true;
      }
    });
  };

  public resetSelectedMarketFilter = () => {
    const { selectedMarketFilter } = symbolsService.context;

    if (selectedMarketFilter) {
      this.context.selectedMarketFilter = this.mapSymbolFiltertoMarketFilter(selectedMarketFilter);
    }
  };

  private mapSymbolFiltertoMarketFilter = (symbolFilter: TSymbolFilter) => {
    switch (symbolFilter) {
      case EMarketFilter.FOREX:
        return EMarketFilter.FOREX;
      case EMarketFilter.INDEX:
        return EMarketFilter.INDEX;
      case EMarketFilter.CRYPTO:
        return EMarketFilter.CRYPTO;
      case EMarketFilter.SHARES:
        return EMarketFilter.SHARES;
      case EMarketFilter.COMMODITIES:
        return EMarketFilter.COMMODITIES;
      default:
        return defaultContext.selectedMarketFilter;
    }
  };

  private fetchMarketAllSymbols = async () => {
    const appType = configService.getAppType();

    if (appType === EAppType.PC && this.context.allSymbols.length > 0) {
      this.context.loading = false;

      return;
    }

    try {
      this.context.loading = true;

      const { result } = await symbolsAPI.allSymbols();

      this.context.allSymbols = result || [];
    } catch {
    } finally {
      this.context.loading = false;
    }
  };
}

export default new SymbolsPreferenceService();
